<template>
    <!-- BS Labs toast: bottom left, dark, one at a time. Mounted once in App.vue; fed by
         useToastStack() (via $toast, @/utils/toast and useToast()). -->
    <div class="ds-toast-stack" role="status" aria-live="polite" aria-relevant="additions">
        <TransitionGroup name="ds-toast" tag="div" class="ds-toast-stack__list">
            <div
                v-for="t in toasts"
                :key="t.id"
                :class="['toast', 'ds-toast', t.type]"
                :role="t.role === 'alert' ? 'alert' : undefined"
                @mouseenter="pause(t.id)"
                @mouseleave="resume(t.id)"
                @focusin="pause(t.id)"
                @focusout="resume(t.id)"
            >
                <span class="toast-icon" aria-hidden="true">
                    <v-icon size="14">{{ ICONS[t.type] }}</v-icon>
                </span>
                <div class="toast-body">
                    <div class="toast-title">{{ t.title }}</div>
                    <div v-if="t.description" class="toast-desc">{{ t.description }}</div>
                    <div v-if="t.actions.length" class="toast-actions">
                        <button v-for="a in t.actions" :key="a.label" type="button" class="toast-action" @click="run(t.id, a.onClick)">
                            {{ a.label }}
                        </button>
                    </div>
                </div>
                <button type="button" class="toast-close" aria-label="Close" @click="dismiss(t.id)">
                    <v-icon size="16">mdi-close</v-icon>
                </button>
            </div>
        </TransitionGroup>
    </div>
</template>

<script setup lang="ts">
import { useToastStack, type ToastId } from '@/composables/useToastStack';

const ICONS = {
    success: 'mdi-check',
    error: 'mdi-close',
    warning: 'mdi-exclamation',
    info: 'mdi-information-variant'
} as const;

const { toasts, dismiss, pause, resume } = useToastStack();

function run(id: ToastId, fn: () => void) {
    // An action (Undo, Retry, View in ...) is the user's answer: the toast goes.
    dismiss(id);
    try {
        fn();
    } catch (e) {
        console.error('Toast action failed:', e);
    }
}
</script>

<style scoped>
/* Layer: v3.3 names --bs-z-toast (260) as the top of its own scale. Vuetify's
   overlays (dialogs, menus, snackbars) stack from 2000 upwards here, so the
   token alone would put toasts under an open dialog - and many toasts are
   fired from inside one. Until the Vuetify overlay layer is mapped onto the
   --bs-z-* scale, the stack keeps the app's existing notification layer. */
.ds-toast-stack {
    position: fixed;
    left: var(--bs-space-6);
    bottom: calc(var(--bs-space-6) + env(safe-area-inset-bottom, 0px));
    z-index: 12000;
    pointer-events: none;
    font-family: var(--bs-font);
}

.ds-toast-stack__list {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--bs-space-3);
}

/* BS Labs toast: dark (#101828), white 13px text, radius 10, 340 wide. */
.toast {
    display: flex;
    align-items: flex-start;
    gap: var(--bs-space-3);
    width: 340px;
    max-width: calc(100vw - 2 * var(--bs-space-4));
    padding: 12px 14px;
    background: var(--bs-inverse);
    border-radius: var(--bs-radius-tile);
    box-shadow: 0 12px 24px -6px rgba(16, 24, 40, 0.3);
    color: #fff;
    font-size: var(--bs-fs-small);
    line-height: 18px;
    pointer-events: auto;
}

.toast-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--bs-success-500);
    color: #fff;
}

.toast.warning .toast-icon {
    background: var(--bs-warning-500);
    color: var(--bs-inverse);
}

.toast.error .toast-icon {
    background: var(--bs-danger-500);
}

.toast.info .toast-icon {
    background: var(--bs-primary-200);
    color: var(--bs-inverse);
}

.toast-body {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
}

.toast-title {
    font-size: var(--bs-fs-small);
    font-weight: 600;
    line-height: 18px;
}

.toast-desc {
    margin-top: 2px;
    font-size: var(--bs-fs-caption);
    line-height: 16px;
    color: var(--bs-border-strong);
}

.toast-actions {
    display: flex;
    gap: var(--bs-space-3);
    margin-top: var(--bs-space-2);
}

.toast-action {
    padding: 0;
    border: 0;
    background: none;
    color: var(--bs-primary-200);
    font: inherit;
    font-size: var(--bs-fs-small);
    font-weight: 600;
    cursor: pointer;
}

.toast-action:hover {
    color: #fff;
    text-decoration: underline;
}

.toast-close {
    display: flex;
    align-self: flex-start;
    padding: 0;
    border: 0;
    background: none;
    color: var(--bs-icon-faint);
    cursor: pointer;
    line-height: 1;
    transition: color var(--dur-fast) ease;
}

.toast-close:hover {
    color: #fff;
}

.toast-action:focus-visible,
.toast-close:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
    border-radius: var(--bs-radius-xs);
}

/* Motion: rises in, slides out left (--dur-panel, --bs-ease). */
.ds-toast-enter-active,
.ds-toast-leave-active,
.ds-toast-move {
    transition:
        opacity var(--dur-panel) var(--bs-ease),
        transform var(--dur-panel) var(--bs-ease);
}

.ds-toast-enter-from {
    opacity: 0;
    transform: translateY(10px);
}

.ds-toast-leave-to {
    opacity: 0;
    transform: translateX(-24px);
}

/* Leaving toasts drop out of the flow so the rest slide into place. */
.ds-toast-leave-active {
    position: absolute;
    left: 0;
}

/* Phones: full width along the bottom. */
@media (max-width: 600px) {
    .ds-toast-stack {
        left: var(--bs-space-4);
        right: var(--bs-space-4);
        bottom: calc(var(--bs-space-4) + env(safe-area-inset-bottom, 0px));
    }

    .ds-toast-stack__list {
        align-items: stretch;
    }

    .toast {
        width: auto;
        max-width: none;
    }
}

@media (prefers-reduced-motion: reduce) {
    .ds-toast-enter-active,
    .ds-toast-leave-active,
    .ds-toast-move {
        transition: none;
    }

    .ds-toast-enter-from,
    .ds-toast-leave-to {
        transform: none;
    }
}
</style>
